import React, { useState } from 'react';
import { Search, TrendingUp, Target, Users, DollarSign, Star, Zap, AlertCircle, CheckCircle, Clock } from 'lucide-react';

const ShopeeAnalyzer = () => {
  const [productData, setProductData] = useState({
    name: '',
    category: '',
    subcategory: '',
    description: ''
  });
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);

  const categories = {
    'Kesehatan': ['Suplemen & Vitamin', 'Obat Herbal', 'Alat Kesehatan', 'Produk Diet', 'Protein & Fitness'],
    'Kecantikan': ['Skincare', 'Makeup', 'Perawatan Rambut', 'Parfum', 'Perawatan Tubuh'],
    'Minuman': ['Minuman Sehat', 'Teh & Kopi', 'Minuman Berenergi', 'Jus & Smoothie', 'Minuman Tradisional']
  };

  const generateAnalysis = () => {
    setLoading(true);
    
    // Simulate AI analysis
    setTimeout(() => {
      const algorithmScore = Math.floor(Math.random() * 40) + 60; // 60-100
      const searchRanking = Math.floor(Math.random() * 30) + 1; // 1-30
      const competitionLevel = ['Rendah', 'Sedang', 'Tinggi'][Math.floor(Math.random() * 3)];
      const growthPrediction = Math.floor(Math.random() * 200) + 50; // 50-250%
      const timelineWeeks = Math.floor(Math.random() * 8) + 4; // 4-12 weeks
      
      const analysisResult = {
        algorithmScore,
        searchRanking,
        competitionLevel,
        growthPrediction,
        timelineWeeks,
        swot: {
          strengths: [
            'Kategori dengan permintaan tinggi',
            'Potensi repeat purchase yang baik',
            'Target audience yang jelas'
          ],
          weaknesses: [
            'Kompetisi harga yang ketat',
            'Membutuhkan trust building',
            'Regulasi produk yang ketat'
          ],
          opportunities: [
            'Tren kesehatan meningkat',
            'Pasar online berkembang',
            'Kolaborasi dengan influencer'
          ],
          threats: [
            'Banyak kompetitor besar',
            'Perubahan algoritma platform',
            'Fluktuasi harga bahan baku'
          ]
        },
        recommendations: {
          pricing: generatePricingRecommendation(productData.category),
          keywords: generateKeywords(productData.name, productData.category),
          affiliates: generateAffiliates(),
          commission: '15-25%',
          adsStrategy: generateAdsStrategy(productData.category),
          displayAds: generateDisplayAdsTarget(productData.category),
          influencers: generateInfluencers(productData.category),
          shopeeAds: generateShopeeAdsKeywords(productData.name, productData.category)
        }
      };
      
      setAnalysis(analysisResult);
      setLoading(false);
    }, 2000);
  };

  const generatePricingRecommendation = (category) => {
    const pricing = {
      'Kesehatan': 'Rp 89.000 - Rp 299.000',
      'Kecantikan': 'Rp 65.000 - Rp 450.000',
      'Minuman': 'Rp 25.000 - Rp 150.000'
    };
    return pricing[category] || 'Rp 50.000 - Rp 200.000';
  };

  const generateKeywords = (productName, category) => {
    const baseKeywords = {
      'Kesehatan': ['sehat alami', 'vitamin terbaik', 'suplemen berkualitas', 'herbal original'],
      'Kecantikan': ['glowing skin', 'anti aging', 'skincare korea', 'makeup tahan lama'],
      'Minuman': ['minuman sehat', 'organic drink', 'fresh juice', 'detox drink']
    };
    return baseKeywords[category] || ['produk berkualitas', 'harga terjangkau'];
  };

  const generateAffiliates = () => {
    return {
      'Level 1 (Rp 1-10 Juta)': [
        { name: '@healthylifeid', account: 'healthylife.indonesia' },
        { name: '@beautyreviewid', account: 'beauty.review.id' },
        { name: '@minumansehatid', account: 'minuman.sehat.id' }
      ],
      'Level 2 (Rp 10-30 Juta)': [
        { name: '@skincarejunkie', account: 'skincare.junkie.id' },
        { name: '@vitaminexpertid', account: 'vitamin.expert.indonesia' },
        { name: '@drinkwellid', account: 'drinkwell.indonesia' }
      ],
      'Level 3 (Rp 30+ Juta)': [
        { name: '@beautyguruid', account: 'beauty.guru.indonesia' },
        { name: '@healthmasterid', account: 'health.master.id' },
        { name: '@beveragekingid', account: 'beverage.king.indonesia' }
      ]
    };
  };

  const generateAdsStrategy = (category) => {
    const strategies = {
      'Kesehatan': 'Fokus pada benefit kesehatan, gunakan testimonial, target audience 25-45 tahun',
      'Kecantikan': 'Tampilkan before-after, gunakan visual menarik, target audience 18-35 tahun',
      'Minuman': 'Highlight kualitas dan rasa, gunakan lifestyle imagery, target audience 20-40 tahun'
    };
    return strategies[category] || 'Strategi umum dengan fokus pada kualitas produk';
  };

  const generateDisplayAdsTarget = (category) => {
    const targets = {
      'Kesehatan': {
        demografi: 'Usia 25-45, pendapatan menengah ke atas',
        interest: 'Kesehatan, fitness, gaya hidup sehat, suplemen'
      },
      'Kecantikan': {
        demografi: 'Wanita 18-35, urban, pendapatan menengah',
        interest: 'Skincare, makeup, K-beauty, self-care'
      },
      'Minuman': {
        demografi: 'Usia 20-40, aktif, health-conscious',
        interest: 'Minuman sehat, organic, detox, lifestyle'
      }
    };
    return targets[category] || { demografi: 'General audience', interest: 'Produk berkualitas' };
  };

  const generateInfluencers = (category) => {
    const influencers = {
      'Kesehatan': [
        { name: 'Dr. Zaidul Akbar', account: '@zaidulakbar', engagement: '12.5%' },
        { name: 'Tya Ariestya', account: '@tya_ariestya', engagement: '11.8%' },
        { name: 'Rachel Venya', account: '@rachelvennya', engagement: '10.2%' }
      ],
      'Kecantikan': [
        { name: 'Suhay Salim', account: '@suhaysalim', engagement: '15.3%' },
        { name: 'Tasya Farasya', account: '@tasyafarasya', engagement: '13.7%' },
        { name: 'Abel Cantika', account: '@abellyc', engagement: '12.1%' }
      ],
      'Minuman': [
        { name: 'Gading Marten', account: '@gadiiing', engagement: '11.9%' },
        { name: 'Nagita Slavina', account: '@raffinagita1717', engagement: '10.8%' },
        { name: 'Syahrini', account: '@princessyahrini', engagement: '10.4%' }
      ]
    };
    return influencers[category] || [];
  };

  const generateShopeeAdsKeywords = (productName, category) => {
    const keywords = {
      'Kesehatan': ['vitamin murah', 'suplemen terbaik', 'herbal asli', 'obat alami'],
      'Kecantikan': ['skincare viral', 'serum glowing', 'cream anti aging', 'makeup korea'],
      'Minuman': ['jus fresh', 'minuman detox', 'teh sehat', 'smoothie diet']
    };
    return keywords[category] || ['produk berkualitas', 'harga murah'];
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreInterpretation = (score) => {
    if (score >= 80) return 'Excellent - Sangat berpotensi sukses';
    if (score >= 60) return 'Good - Potensi sukses dengan optimasi';
    return 'Need Improvement - Perlu perbaikan signifikan';
  };

  return (
    <div className="max-w-6xl mx-auto p-6 bg-gradient-to-br from-purple-50 to-blue-50 min-h-screen">
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-gray-800 mb-2">
          🛍️ Shopee Algorithm Analyzer
        </h1>
        <p className="text-gray-600 text-lg">Generator Analisis Komprehensif untuk Seller Shopee</p>
      </div>

      {/* Input Section */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <h2 className="text-2xl font-semibold mb-4 flex items-center">
          <Search className="mr-2 text-blue-600" />
          📝 Input Produk Sederhana
        </h2>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Nama Produk</label>
            <input
              type="text"
              value={productData.name}
              onChange={(e) => setProductData({...productData, name: e.target.value})}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Contoh: Vitamin C 1000mg"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">Kategori</label>
            <select
              value={productData.category}
              onChange={(e) => setProductData({...productData, category: e.target.value, subcategory: ''})}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Pilih Kategori</option>
              {Object.keys(categories).map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">Sub Kategori</label>
            <select
              value={productData.subcategory}
              onChange={(e) => setProductData({...productData, subcategory: e.target.value})}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              disabled={!productData.category}
            >
              <option value="">Pilih Sub Kategori</option>
              {productData.category && categories[productData.category].map(sub => (
                <option key={sub} value={sub}>{sub}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">Deskripsi Singkat</label>
            <textarea
              value={productData.description}
              onChange={(e) => setProductData({...productData, description: e.target.value})}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              rows="3"
              placeholder="Deskripsi produk Anda..."
            />
          </div>
        </div>
        
        <button
          onClick={generateAnalysis}
          disabled={!productData.name || !productData.category || loading}
          className="mt-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-3 rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? '🤖 AI Sedang Menganalisis...' : '🚀 Analisis Sekarang'}
        </button>
      </div>

      {/* Analysis Results */}
      {analysis && (
        <div className="space-y-6">
          {/* Score Dashboard */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-2xl font-semibold mb-4 flex items-center">
              <TrendingUp className="mr-2 text-green-600" />
              📊 Dashboard Hasil Komprehensif
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-4 rounded-lg">
                <h3 className="font-semibold">Algorithm Score</h3>
                <p className="text-3xl font-bold">{analysis.algorithmScore}/100</p>
                <p className="text-sm opacity-90">{getScoreInterpretation(analysis.algorithmScore)}</p>
              </div>
              
              <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-4 rounded-lg">
                <h3 className="font-semibold">Prediksi Ranking</h3>
                <p className="text-3xl font-bold">#{analysis.searchRanking}</p>
                <p className="text-sm opacity-90">Posisi di hasil pencarian</p>
              </div>
              
              <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white p-4 rounded-lg">
                <h3 className="font-semibold">Growth Prediction</h3>
                <p className="text-3xl font-bold">{analysis.growthPrediction}%</p>
                <p className="text-sm opacity-90">Peningkatan penjualan</p>
              </div>
              
              <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-4 rounded-lg">
                <h3 className="font-semibold">Timeline Results</h3>
                <p className="text-3xl font-bold">{analysis.timelineWeeks} weeks</p>
                <p className="text-sm opacity-90">Waktu mencapai hasil</p>
              </div>
            </div>
          </div>

          {/* SWOT Analysis */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-2xl font-semibold mb-4">🎯 SWOT Analysis</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="border border-green-200 rounded-lg p-4">
                <h3 className="font-semibold text-green-700 mb-2 flex items-center">
                  <CheckCircle className="mr-1 w-4 h-4" /> Strengths
                </h3>
                <ul className="space-y-1 text-sm">
                  {analysis.swot.strengths.map((item, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="text-green-500 mr-2">•</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="border border-red-200 rounded-lg p-4">
                <h3 className="font-semibold text-red-700 mb-2 flex items-center">
                  <AlertCircle className="mr-1 w-4 h-4" /> Weaknesses
                </h3>
                <ul className="space-y-1 text-sm">
                  {analysis.swot.weaknesses.map((item, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="text-red-500 mr-2">•</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-700 mb-2 flex items-center">
                  <Zap className="mr-1 w-4 h-4" /> Opportunities
                </h3>
                <ul className="space-y-1 text-sm">
                  {analysis.swot.opportunities.map((item, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="text-blue-500 mr-2">•</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="border border-orange-200 rounded-lg p-4">
                <h3 className="font-semibold text-orange-700 mb-2 flex items-center">
                  <AlertCircle className="mr-1 w-4 h-4" /> Threats
                </h3>
                <ul className="space-y-1 text-sm">
                  {analysis.swot.threats.map((item, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="text-orange-500 mr-2">•</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Action Plan */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-2xl font-semibold mb-4 flex items-center">
              <Target className="mr-2 text-purple-600" />
              💡 Rencana Aksi AI-Generated
            </h2>
            
            <div className="space-y-4">
              <div className="border-l-4 border-red-500 pl-4">
                <h3 className="font-semibold text-red-700 flex items-center mb-2">
                  🔴 Priority 1 - Critical (Week 1-2)
                </h3>
                <ul className="space-y-1 text-sm">
                  <li>• Upload foto produk berkualitas tinggi (min 5 foto berbeda)</li>
                  <li>• Optimasi keyword & title produk</li>
                  <li>• Perbaikan deskripsi produk dengan benefit yang jelas</li>
                  <li>• Set up basic customer service response</li>
                </ul>
              </div>
              
              <div className="border-l-4 border-yellow-500 pl-4">
                <h3 className="font-semibold text-yellow-700 flex items-center mb-2">
                  🟡 Priority 2 - Important (Week 3-4)
                </h3>
                <ul className="space-y-1 text-sm">
                  <li>• Ikuti flash sale dan promo Shopee</li>
                  <li>• Implementasi strategi pricing kompetitif</li>
                  <li>• Optimasi customer service dan response time</li>
                  <li>• Mulai kampanye affiliate marketing</li>
                </ul>
              </div>
              
              <div className="border-l-4 border-green-500 pl-4">
                <h3 className="font-semibold text-green-700 flex items-center mb-2">
                  🟢 Priority 3 - Growth (Month 2+)
                </h3>
                <ul className="space-y-1 text-sm">
                  <li>• Kampanye live streaming secara rutin</li>
                  <li>• Strategi generasi review positif</li>
                  <li>• Kreasi bundle produk dan cross-selling</li>
                  <li>• Kolaborasi dengan influencer besar</li>
                </ul>
              </div>
            </div>
          </div>

          {/* AI Recommendations */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-2xl font-semibold mb-4 flex items-center">
              <Star className="mr-2 text-yellow-600" />
              📊 Saran AI-Generated Lengkap
            </h2>
            
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Pricing & Keywords */}
              <div className="space-y-4">
                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold flex items-center mb-2">
                    <DollarSign className="mr-1 w-4 h-4 text-green-600" />
                    Harga Disarankan
                  </h3>
                  <p className="text-lg font-bold text-green-600">{analysis.recommendations.pricing}</p>
                </div>
                
                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold mb-2">🔑 Keywords & Title Disarankan</h3>
                  <div className="flex flex-wrap gap-2">
                    {analysis.recommendations.keywords.map((keyword, idx) => (
                      <span key={idx} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm">
                        {keyword}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold mb-2">💰 Komisi Affiliator</h3>
                  <p className="text-lg font-bold text-purple-600">{analysis.recommendations.commission}</p>
                </div>
              </div>

              {/* Affiliates */}
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold mb-2">👥 Affiliator Disarankan</h3>
                {Object.entries(analysis.recommendations.affiliates).map(([level, affiliates]) => (
                  <div key={level} className="mb-3">
                    <h4 className="font-medium text-sm text-gray-700 mb-1">{level}</h4>
                    {affiliates.map((affiliate, idx) => (
                      <div key={idx} className="text-sm bg-gray-50 p-2 rounded mb-1">
                        <span className="font-medium">{affiliate.name}</span>
                        <span className="text-gray-600 ml-2">@{affiliate.account}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            {/* Ads Strategy */}
            <div className="mt-6 space-y-4">
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold mb-2">📢 Strategi Ads Detail</h3>
                <p className="text-sm text-gray-700">{analysis.recommendations.adsStrategy}</p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold mb-2">🎯 Shopee Display Ads Target</h3>
                  <div className="text-sm space-y-1">
                    <p><strong>Demografi:</strong> {analysis.recommendations.displayAds.demografi}</p>
                    <p><strong>Interest:</strong> {analysis.recommendations.displayAds.interest}</p>
                  </div>
                </div>
                
                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold mb-2">🔍 Shopee Ads Keywords</h3>
                  <div className="flex flex-wrap gap-2">
                    {analysis.recommendations.shopeeAds.map((keyword, idx) => (
                      <span key={idx} className="bg-orange-100 text-orange-800 px-2 py-1 rounded text-sm">
                        {keyword}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Influencer Recommendations */}
            <div className="mt-6 border rounded-lg p-4">
              <h3 className="font-semibold mb-3">🌟 Influencer Marketing (15 Influencer Disarankan)</h3>
              <div className="grid md:grid-cols-3 gap-2">
                {analysis.recommendations.influencers.slice(0, 15).map((influencer, idx) => (
                  <div key={idx} className="bg-gradient-to-r from-pink-50 to-purple-50 p-3 rounded text-sm">
                    <div className="font-medium">{influencer.name}</div>
                    <div className="text-gray-600">@{influencer.account.replace('@', '')}</div>
                    <div className="text-green-600 font-medium">ER: {influencer.engagement}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShopeeAnalyzer;